import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useBilling } from '../../hooks/useBilling';
import { Receipt, Download, Eye, Calendar, CreditCard, CheckCircle, Clock, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

interface Invoice {
  id: string;
  number?: string;
  date: string;
  amount: number;
  currency?: string;
  status: string;
  description: string;
  pdfUrl?: string;
  hostedUrl?: string;
}

interface BillingHistoryProps {
  invoices?: Invoice[];
}

export function BillingHistory({ invoices = [] }: BillingHistoryProps) {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const { createPortalSession, downloadInvoice, isDownloadingInvoice } = useBilling();

  const handleViewAllInvoices = async () => {
    try {
      await createPortalSession({
        returnUrl: window.location.href
      });
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      await downloadInvoice(invoiceId);
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'default' as const;
      case 'open': return 'secondary' as const;
      case 'pending': return 'secondary' as const;
      case 'void': return 'destructive' as const;
      case 'draft': return 'secondary' as const;
      default: return 'secondary' as const;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'open': return <Clock className="w-3 h-3 mr-1" />;
      case 'pending': return <Clock className="w-3 h-3 mr-1" />;
      case 'void': return <AlertCircle className="w-3 h-3 mr-1" />;
      case 'draft': return <AlertCircle className="w-3 h-3 mr-1" />;
      default: return <Clock className="w-3 h-3 mr-1" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
              <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Billing History</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">View and download your past invoices</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={handleViewAllInvoices}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>

        {/* Billing History Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Invoice</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{invoice.number ? `#${invoice.number}` : `#${invoice.id.substring(0, 8)}`}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{invoice.description}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-900 dark:text-gray-100">{formatDate(invoice.date)}</td>
                  <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">{formatAmount(invoice.amount, invoice.currency)}</td>
                  <td className="py-3 px-4">
                    <Badge variant={getStatusColor(invoice.status)}>
                      {getStatusIcon(invoice.status)}
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedInvoice(invoice)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDownloadInvoice(invoice.id)}
                        disabled={isDownloadingInvoice}
                      >
                        {isDownloadingInvoice ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {invoices.length === 0 && (
          <div className="text-center py-12">
            <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600 mb-4">Your billing history will appear here once you have invoices.</p>
            <Button variant="outline" onClick={handleViewAllInvoices}>
              <Eye className="w-4 h-4 mr-2" />
              Manage Billing
            </Button>
          </div>
        )}
      </Card>

      {/* Payment Methods */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
              <p className="text-sm text-gray-600">Manage your payment methods in the billing portal</p>
            </div>
          </div>
          <Button onClick={handleViewAllInvoices}>
            Manage Payment Methods
          </Button>
        </div>

        <div className="text-center py-8">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            Manage your payment methods securely through Stripe's billing portal.
          </p>
          <Button variant="outline" onClick={handleViewAllInvoices}>
            <CreditCard className="w-4 h-4 mr-2" />
            Open Billing Portal
          </Button>
        </div>
      </Card>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Invoice {selectedInvoice.number ? `#${selectedInvoice.number}` : `#${selectedInvoice.id.substring(0, 8)}`}
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedInvoice(null)}
                >
                  ×
                </Button>
              </div>

              <div className="space-y-6">
                {/* Invoice Header */}
                <div className="flex justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Crutan AI</h3>
                    <p className="text-sm text-gray-600">AI Marketing Intelligence Platform</p>
                  </div>
                  <div className="text-right">
                    <Badge variant={getStatusColor(selectedInvoice.status)}>
                      {getStatusIcon(selectedInvoice.status)}
                      {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
                    </Badge>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDate(selectedInvoice.date)}
                    </p>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="border-t border-b border-gray-200 py-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{selectedInvoice.description}</span>
                    <span className="font-medium">
                      {formatAmount(selectedInvoice.amount, selectedInvoice.currency)}
                    </span>
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatAmount(selectedInvoice.amount, selectedInvoice.currency)}</span>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <Button 
                    className="flex-1"
                    onClick={() => handleDownloadInvoice(selectedInvoice.id)}
                    disabled={isDownloadingInvoice}
                  >
                    {isDownloadingInvoice ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {isDownloadingInvoice ? 'Downloading...' : 'Download PDF'}
                  </Button>
                  {selectedInvoice.hostedUrl && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => window.open(selectedInvoice.hostedUrl, '_blank')}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Online
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
} 